import React, { useMemo, useState, createElement } from 'react';
import { CalendarIcon, BuildingIcon, TrendingUpIcon, UsersIcon, LayoutGridIcon, ImageIcon, MonitorIcon, TableIcon, CalendarDaysIcon, DownloadIcon } from 'lucide-react';
import { User, Store, BrandRequest, MediaType, BookingSlot } from '../data/types';
import { Header } from '../components/layout/Header';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
const BRAND_COLORS = [{
  bg: 'bg-amber-100 border-amber-300',
  text: 'text-amber-900',
  dot: 'bg-amber-500'
}, {
  bg: 'bg-sky-100 border-sky-300',
  text: 'text-sky-900',
  dot: 'bg-sky-500'
}, {
  bg: 'bg-rose-100 border-rose-300',
  text: 'text-rose-900',
  dot: 'bg-rose-500'
}, {
  bg: 'bg-emerald-100 border-emerald-300',
  text: 'text-emerald-900',
  dot: 'bg-emerald-500'
}, {
  bg: 'bg-violet-100 border-violet-300',
  text: 'text-violet-900',
  dot: 'bg-violet-500'
}, {
  bg: 'bg-orange-100 border-orange-300',
  text: 'text-orange-900',
  dot: 'bg-orange-500'
}, {
  bg: 'bg-teal-100 border-teal-300',
  text: 'text-teal-900',
  dot: 'bg-teal-500'
}, {
  bg: 'bg-pink-100 border-pink-300',
  text: 'text-pink-900',
  dot: 'bg-pink-500'
}, {
  bg: 'bg-indigo-100 border-indigo-300',
  text: 'text-indigo-900',
  dot: 'bg-indigo-500'
}, {
  bg: 'bg-lime-100 border-lime-300',
  text: 'text-lime-900',
  dot: 'bg-lime-500'
}, {
  bg: 'bg-cyan-100 border-cyan-300',
  text: 'text-cyan-900',
  dot: 'bg-cyan-500'
}, {
  bg: 'bg-fuchsia-100 border-fuchsia-300',
  text: 'text-fuchsia-900',
  dot: 'bg-fuchsia-500'
}, {
  bg: 'bg-yellow-100 border-yellow-300',
  text: 'text-yellow-900',
  dot: 'bg-yellow-500'
}, {
  bg: 'bg-red-100 border-red-300',
  text: 'text-red-900',
  dot: 'bg-red-500'
}, {
  bg: 'bg-blue-100 border-blue-300',
  text: 'text-blue-900',
  dot: 'bg-blue-500'
}];
function getBrandColor(brandName: string, allBrands: string[]) {
  const idx = allBrands.indexOf(brandName);
  return BRAND_COLORS[idx % BRAND_COLORS.length];
}
function generateWeekDatesForCalendar(weekNumber: number): {
  start: string;
  end: string;
} {
  const baseDate = new Date('2026-03-03');
  const startDate = new Date(baseDate);
  startDate.setDate(baseDate.getDate() + (weekNumber - 1) * 7);
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short'
  };
  return {
    start: startDate.toLocaleDateString('en-ZA', opts),
    end: endDate.toLocaleDateString('en-ZA', opts)
  };
}
interface CampaignCalendarPageProps {
  user: User;
  stores: Store[];
  brandRequests: BrandRequest[];
  draftCount: number;
  onLogout: () => void;
  onNavigate: (view: any) => void;
  activityLog?: any;
  brands?: any[];
}
interface AggregatedCampaign {
  id: string;
  brandName: string;
  campaignName?: string;
  stores: {
    id: string;
    name: string;
  }[];
  mediaTypes: MediaType[];
  weekNumber: number;
  endWeekNumber?: number;
  weekStartDate: string;
  weekEndDate: string;
  slotCount: number;
  totalRevenue: number;
  source: 'slots' | 'request' | 'both';
  hasAcceptedRequest: boolean;
  artworkStatus: 'none' | 'partial' | 'uploaded' | 'approved';
}
const mediaTypeIcon: Record<MediaType, React.ReactNode> = {
  'Digital Outposts': <LayoutGridIcon className="w-3.5 h-3.5" data-id="element-1598" />,
  'Digital Drive Aisle': <ImageIcon className="w-3.5 h-3.5" data-id="element-1599" />,
  'Digital Windows': <MonitorIcon className="w-3.5 h-3.5" data-id="element-1600" />
};
function formatWeekDates(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short'
  };
  return `${s.toLocaleDateString('en-ZA', opts)} – ${e.toLocaleDateString('en-ZA', opts)}`;
}
function formatCurrency(amount: number): string {
  return `R ${amount.toLocaleString('en-ZA')}`;
}
export function CampaignCalendarPage({
  user,
  stores,
  brandRequests,
  draftCount,
  onLogout,
  onNavigate,
  activityLog,
  brands
}: CampaignCalendarPageProps) {
  const [brandFilter, setBrandFilter] = useState('all');
  const [weekFilter, setWeekFilter] = useState('all');
  const [mediaFilter, setMediaFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'table' | 'calendar'>('table');
  const [hoveredCampaign, setHoveredCampaign] = useState<string | null>(null);
  // Brand privacy: determine if user is a brand and their brand name
  const isBrand = user.role === 'brand';
  const userBrandName = user.company || user.name;
  // Aggregate campaigns from booked slots
  const campaigns = useMemo(() => {
    const campaignMap = new Map<string, AggregatedCampaign>();
    // 1. Collect booked slots from all stores
    stores.forEach(store => {
      store.slots.filter(slot => slot.status === 'booked' && slot.brandName).forEach(slot => {
        const key = `${slot.brandName}-week-${slot.weekNumber}`;
        const existing = campaignMap.get(key);
        // Simulate artwork status for demo purposes based on slot ID hash
        const hash = slot.id.split('').reduce((a, b) => {
          a = (a << 5) - a + b.charCodeAt(0);
          return a & a;
        }, 0);
        const mockArtworkStatus = hash % 3 === 0 ? 'uploaded' : hash % 5 === 0 ? 'approved' : 'none';
        if (existing) {
          if (!existing.stores.find(s => s.id === store.id)) {
            existing.stores.push({
              id: store.id,
              name: store.name
            });
          }
          if (!existing.mediaTypes.includes(slot.mediaType)) {
            existing.mediaTypes.push(slot.mediaType);
          }
          existing.slotCount++;
          existing.totalRevenue += slot.price;
          // Aggregate artwork status
          if (existing.artworkStatus === 'none' && mockArtworkStatus !== 'none') {
            existing.artworkStatus = 'partial';
          } else if (existing.artworkStatus === 'uploaded' && mockArtworkStatus === 'none') {
            existing.artworkStatus = 'partial';
          }
        } else {
          campaignMap.set(key, {
            id: key,
            brandName: slot.brandName!,
            stores: [{
              id: store.id,
              name: store.name
            }],
            mediaTypes: [slot.mediaType],
            weekNumber: slot.weekNumber,
            weekStartDate: slot.weekStartDate,
            weekEndDate: slot.weekEndDate,
            slotCount: 1,
            totalRevenue: slot.price,
            source: 'slots',
            hasAcceptedRequest: false,
            artworkStatus: mockArtworkStatus as any
          });
        }
      });
    });
    // 2. Merge accepted brand requests (add campaign names, mark source)
    brandRequests.filter(r => r.status === 'accepted' || r.status === 'accepted-with-conditions').forEach(req => {
      const key = `${req.brandName}-week-${req.weekNumber}`;
      const existing = campaignMap.get(key);
      if (existing) {
        existing.campaignName = req.campaignName || existing.campaignName;
        existing.source = 'both';
        existing.hasAcceptedRequest = true;
      } else {
        campaignMap.set(key, {
          id: key,
          brandName: req.brandName,
          campaignName: req.campaignName,
          stores: req.stores.map(s => ({
            id: s.storeId,
            name: s.storeName
          })),
          mediaTypes: req.mediaTypes || [req.mediaType],
          weekNumber: req.weekNumber,
          endWeekNumber: req.endWeekNumber,
          weekStartDate: req.weekStartDate,
          weekEndDate: req.weekEndDate,
          slotCount: req.stores.length,
          totalRevenue: 0,
          source: 'request',
          hasAcceptedRequest: true,
          artworkStatus: 'none'
        });
      }
    });
    return Array.from(campaignMap.values()).sort((a, b) => a.weekNumber - b.weekNumber || a.brandName.localeCompare(b.brandName));
  }, [stores, brandRequests]);
  // Unique brand names for filter
  const brandOptions = useMemo(() => {
    const names = [...new Set(campaigns.map(c => c.brandName))].sort();
    return names.map(n => ({
      value: n,
      label: n
    }));
  }, [campaigns]);
  // Week options
  const weekOptions = useMemo(() => {
    const weeks = [...new Set(campaigns.map(c => c.weekNumber))].sort((a, b) => a - b);
    return weeks.map(w => ({
      value: w.toString(),
      label: `Week ${w}`
    }));
  }, [campaigns]);
  // Media type options
  const mediaOptions: {
    value: string;
    label: string;
  }[] = [{
    value: 'Digital Outposts',
    label: 'Digital Outposts'
  }, {
    value: 'Digital Drive Aisle',
    label: 'Digital Drive Aisle'
  }, {
    value: 'Digital Windows',
    label: 'Digital Windows'
  }];
  // Apply filters
  const filtered = useMemo(() => {
    return campaigns.filter(c => {
      if (brandFilter !== 'all' && c.brandName !== brandFilter) return false;
      if (weekFilter !== 'all' && c.weekNumber.toString() !== weekFilter) return false;
      if (mediaFilter !== 'all' && !c.mediaTypes.includes(mediaFilter as MediaType)) return false;
      return true;
    });
  }, [campaigns, brandFilter, weekFilter, mediaFilter]);
  // Brand-privacy-aware display data: anonymize other brands for brand users
  const displayCampaigns = useMemo(() => {
    if (!isBrand) return filtered;
    return filtered.map(c => {
      if (c.brandName === userBrandName) return c;
      // Anonymize other brands
      return {
        ...c,
        brandName: 'Unavailable',
        campaignName: undefined,
        totalRevenue: 0,
        artworkStatus: 'none' as const,
        hasAcceptedRequest: false
      };
    });
  }, [filtered, isBrand, userBrandName]);
  // Summary stats — for brands, only count their own campaigns
  const ownCampaigns = useMemo(() => {
    if (!isBrand) return filtered;
    return filtered.filter(c => c.brandName === userBrandName);
  }, [filtered, isBrand, userBrandName]);
  const totalCampaigns = isBrand ? ownCampaigns.length : displayCampaigns.length;
  const uniqueBrands = isBrand ? 1 : new Set(displayCampaigns.map(c => c.brandName)).size;
  const uniqueStores = new Set((isBrand ? ownCampaigns : displayCampaigns).flatMap(c => c.stores.map(s => s.id))).size;
  const totalRevenue = (isBrand ? ownCampaigns : displayCampaigns).reduce((sum, c) => sum + c.totalRevenue, 0);
  const hasActiveFilters = brandFilter !== 'all' || weekFilter !== 'all' || mediaFilter !== 'all';
  // Calendar data: group campaigns by brand (using display data)
  const calendarData = useMemo(() => {
    const brandMap = new Map<string, AggregatedCampaign[]>();
    displayCampaigns.forEach(c => {
      const existing = brandMap.get(c.brandName) || [];
      existing.push(c);
      brandMap.set(c.brandName, existing);
    });
    // For brand users, put own brand first
    return Array.from(brandMap.entries()).sort((a, b) => {
      if (isBrand) {
        if (a[0] === userBrandName) return -1;
        if (b[0] === userBrandName) return 1;
      }
      return b[1].length - a[1].length;
    }).map(([brand, campaigns]) => ({
      brand,
      campaigns
    }));
  }, [displayCampaigns, isBrand, userBrandName]);
  const allBrandNames = useMemo(() => calendarData.map(d => d.brand), [calendarData]);
  const weeks = Array.from({
    length: 12
  }, (_, i) => i + 1);
  const handleExportCSV = () => {
    const isAdmin = user.role === 'admin';
    const headers = ['Week', 'Start Date', 'End Date', 'Brand', 'Campaign Name', 'Stores', 'Media Types', 'Slot Count', ...(isAdmin ? [] : ['Revenue']), 'Status', 'Artwork Status'];
    // For brand users, only export their own campaigns
    const exportData = isBrand ? ownCampaigns : filtered;
    const csvContent = [headers.join(','), ...exportData.map(c => [c.weekNumber, c.weekStartDate, c.weekEndDate, `"${c.brandName}"`, `"${c.campaignName || ''}"`, `"${c.stores.map(s => s.name).join('; ')}"`, `"${c.mediaTypes.join('; ')}"`, c.slotCount, ...(isAdmin ? [] : [c.totalRevenue]), c.hasAcceptedRequest ? 'Confirmed' : 'Booked', c.artworkStatus].join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `campaigns_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return <div className="min-h-screen w-full bg-white flex flex-col" data-id="element-1601">
      <Header user={user} onLogout={onLogout} currentView="campaign-calendar" onNavigate={onNavigate} draftCount={isBrand ? 0 : draftCount} stores={stores} brands={brands} brandRequests={brandRequests} activityEntries={activityLog?.entries} unreadCount={activityLog?.unreadCount} onMarkAllRead={activityLog?.markAllAsRead} onMarkRead={activityLog?.markAsRead} data-id="element-1602" />

      {/* Summary stats */}
      <div className="border-b border-border bg-surface-warm" data-id="element-1603">
        <div className="px-4 md:px-6 py-4 flex flex-wrap items-center gap-4 md:gap-8" data-id="element-1604">
          <div className="flex items-center gap-2.5" data-id="element-1605">
            <div className="w-8 h-8 rounded-lg bg-primary/5 flex items-center justify-center" data-id="element-1606">
              <CalendarIcon className="w-4 h-4 text-primary" data-id="element-1607" />
            </div>
            <div data-id="element-1608">
              <p className="text-2xl font-bold text-text-primary leading-none" data-id="element-1609">
                {totalCampaigns}
              </p>
              <p className="text-xs text-text-tertiary mt-0.5" data-id="element-1610">Campaigns</p>
            </div>
          </div>
          {!isBrand && <>
              <div className="w-px h-10 bg-border" data-id="element-1611" />
              <div className="flex items-center gap-2.5" data-id="element-1612">
                <div className="w-8 h-8 rounded-lg bg-primary/5 flex items-center justify-center" data-id="element-1613">
                  <UsersIcon className="w-4 h-4 text-primary" data-id="element-1614" />
                </div>
                <div data-id="element-1615">
                  <p className="text-2xl font-bold text-text-primary leading-none" data-id="element-1616">
                    {uniqueBrands}
                  </p>
                  <p className="text-xs text-text-tertiary mt-0.5" data-id="element-1617">Brands</p>
                </div>
              </div>
            </>}
          <div className="w-px h-10 bg-border" data-id="element-1618" />
          <div className="flex items-center gap-2.5" data-id="element-1619">
            <div className="w-8 h-8 rounded-lg bg-primary/5 flex items-center justify-center" data-id="element-1620">
              <BuildingIcon className="w-4 h-4 text-primary" data-id="element-1621" />
            </div>
            <div data-id="element-1622">
              <p className="text-2xl font-bold text-text-primary leading-none" data-id="element-1623">
                {uniqueStores}
              </p>
              <p className="text-xs text-text-tertiary mt-0.5" data-id="element-1624">Stores</p>
            </div>
          </div>
          {user.role !== 'admin' && <>
              <div className="w-px h-10 bg-border" data-id="element-1625" />
              <div className="flex items-center gap-2.5" data-id="element-1626">
                <div className="w-8 h-8 rounded-lg bg-primary/5 flex items-center justify-center" data-id="element-1627">
                  <TrendingUpIcon className="w-4 h-4 text-primary" data-id="element-1628" />
                </div>
                <div data-id="element-1629">
                  <p className="text-2xl font-bold text-text-primary leading-none" data-id="element-1630">
                    {formatCurrency(totalRevenue)}
                  </p>
                  <p className="text-xs text-text-tertiary mt-0.5" data-id="element-1631">
                    Total Revenue
                  </p>
                </div>
              </div>
            </>}
        </div>
      </div>

      {/* Filters + View Toggle */}
      <div className="px-4 md:px-6 py-3 border-b border-border bg-white flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1632">
        <div className="flex flex-wrap items-center gap-2 md:gap-4" data-id="element-1633">
          {!isBrand && <div className="w-full sm:w-48" data-id="element-1634">
              <Select label="Brand" placeholder="All Brands" value={brandFilter} onChange={e => setBrandFilter(e.target.value)} options={brandOptions} data-id="element-1635" />
            </div>}
          <div className="w-full sm:w-40" data-id="element-1636">
            <Select label="Week" placeholder="All Weeks" value={weekFilter} onChange={e => setWeekFilter(e.target.value)} options={weekOptions} data-id="element-1637" />
          </div>
          <div className="w-full sm:w-48" data-id="element-1638">
            <Select label="Media Type" placeholder="All Types" value={mediaFilter} onChange={e => setMediaFilter(e.target.value)} options={mediaOptions} data-id="element-1639" />
          </div>
          {hasActiveFilters && <button onClick={() => {
          setBrandFilter('all');
          setWeekFilter('all');
          setMediaFilter('all');
        }} className="mt-4 text-sm text-primary hover:text-primary-dark font-medium transition-colors" data-id="element-1640">
              Clear All
            </button>}
        </div>

        {/* View toggle & Export */}
        <div className="flex items-center gap-3 self-end md:self-auto" data-id="element-1641">
          <button onClick={handleExportCSV} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-white text-text-secondary hover:text-text-primary hover:bg-surface transition-colors" data-id="element-1642">
            <DownloadIcon className="w-3.5 h-3.5" data-id="element-1643" />
            <span className="hidden sm:inline" data-id="element-1644">Export CSV</span>
          </button>
          <div className="flex items-center gap-1 bg-surface rounded-md border border-border p-0.5" data-id="element-1645">
            <button onClick={() => setViewMode('table')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'table' ? 'bg-white text-primary shadow-sm border border-border' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-1646">
              <TableIcon className="w-3.5 h-3.5" data-id="element-1647" />
              <span className="hidden sm:inline" data-id="element-1648">Table</span>
            </button>
            <button onClick={() => setViewMode('calendar')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'calendar' ? 'bg-white text-primary shadow-sm border border-border' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-1649">
              <CalendarDaysIcon className="w-3.5 h-3.5" data-id="element-1650" />
              <span className="hidden sm:inline" data-id="element-1651">Calendar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 overflow-auto" data-id="element-1652">
        {filtered.length === 0 ? <div className="flex flex-col items-center justify-center py-24 text-center" data-id="element-1653">
            <CalendarIcon className="w-12 h-12 text-text-tertiary mb-4" data-id="element-1654" />
            <h3 className="text-lg font-semibold text-text-primary mb-1" data-id="element-1655">
              No campaigns found
            </h3>
            <p className="text-sm text-text-secondary" data-id="element-1656">
              {hasActiveFilters ? 'Try adjusting your filters to see more results.' : 'No confirmed campaigns have been scheduled yet.'}
            </p>
          </div> : viewMode === 'table' ? <div className="overflow-x-auto" data-id="element-1657">
            <table className="w-full text-left border-collapse min-w-[1000px]" data-id="element-1658">
              <thead className="sticky top-0 z-10" data-id="element-1659">
                <tr className="bg-surface border-b border-border" data-id="element-1660">
                  <th className="px-4 md:px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1661">
                    Week
                  </th>
                  <th className="px-4 md:px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1662">
                    Brand
                  </th>
                  <th className="px-4 md:px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1663">
                    Campaign
                  </th>
                  <th className="px-4 md:px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1664">
                    Stores
                  </th>
                  <th className="px-4 md:px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1665">
                    Media
                  </th>
                  <th className="px-4 md:px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1666">
                    Slots
                  </th>
                  {user.role !== 'admin' && <th className="px-4 md:px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide text-right" data-id="element-1667">
                      Revenue
                    </th>}
                  <th className="px-4 md:px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide text-center" data-id="element-1668">
                    Artwork
                  </th>
                  <th className="px-4 md:px-6 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide text-center" data-id="element-1669">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border" data-id="element-1670">
                {displayCampaigns.map(campaign => {
              const isUnavailable = campaign.brandName === 'Unavailable';
              return <tr key={campaign.id} className={`transition-colors ${isUnavailable ? 'bg-surface/30' : 'hover:bg-surface/40'}`} data-id="element-1671">
                      <td className="px-4 md:px-6 py-3.5" data-id="element-1672">
                        <div className="text-sm font-semibold text-text-primary" data-id="element-1673">
                          Week {campaign.weekNumber}
                          {campaign.endWeekNumber && campaign.endWeekNumber !== campaign.weekNumber ? ` – ${campaign.endWeekNumber}` : ''}
                        </div>
                        <div className="text-xs text-text-tertiary mt-0.5" data-id="element-1674">
                          {formatWeekDates(campaign.weekStartDate, campaign.weekEndDate)}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3.5" data-id="element-1675">
                        {isUnavailable ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-surface-warm text-text-tertiary border border-border rounded-md" data-id="element-1676">
                            Unavailable
                          </span> : <span className="text-sm font-medium text-text-primary" data-id="element-1677">
                            {campaign.brandName}
                          </span>}
                      </td>
                      <td className="px-4 md:px-6 py-3.5" data-id="element-1678">
                        {isUnavailable ? <span className="text-sm text-text-tertiary" data-id="element-1679">—</span> : campaign.campaignName ? <span className="text-sm text-text-primary" data-id="element-1680">
                            {campaign.campaignName}
                          </span> : <span className="text-sm text-text-tertiary italic" data-id="element-1681">
                            No name
                          </span>}
                      </td>
                      <td className="px-4 md:px-6 py-3.5" data-id="element-1682">
                        {campaign.stores.length <= 2 ? <div className="space-y-0.5" data-id="element-1683">
                            {campaign.stores.map(s => <div key={s.id} className="text-sm text-text-secondary" data-id="element-1684">
                                {s.name}
                              </div>)}
                          </div> : <div data-id="element-1685">
                            <div className="text-sm text-text-secondary" data-id="element-1686">
                              {campaign.stores[0].name}
                            </div>
                            <div className="text-xs text-text-tertiary mt-0.5" data-id="element-1687">
                              + {campaign.stores.length - 1} more
                            </div>
                          </div>}
                      </td>
                      <td className="px-4 md:px-6 py-3.5" data-id="element-1688">
                        <div className="flex flex-wrap gap-1.5" data-id="element-1689">
                          {campaign.mediaTypes.map(mt => <span key={mt} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-text-secondary bg-surface-warm border border-border rounded-sm" data-id="element-1690">
                              {mediaTypeIcon[mt]}
                              <span className="hidden lg:inline" data-id="element-1691">
                                {mt.replace('Digital ', '')}
                              </span>
                            </span>)}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3.5" data-id="element-1692">
                        <span className={`text-sm font-medium ${isUnavailable ? 'text-text-tertiary' : 'text-text-primary'}`} data-id="element-1693">
                          {isUnavailable ? '—' : campaign.slotCount}
                        </span>
                      </td>
                      {user.role !== 'admin' && <td className="px-4 md:px-6 py-3.5 text-right" data-id="element-1694">
                          <span className={`text-sm font-medium ${isUnavailable ? 'text-text-tertiary' : 'text-text-primary'}`} data-id="element-1695">
                            {isUnavailable ? '—' : formatCurrency(campaign.totalRevenue)}
                          </span>
                        </td>}
                      <td className="px-4 md:px-6 py-3.5 text-center" data-id="element-1696">
                        {isUnavailable ? <span className="text-sm text-text-tertiary" data-id="element-1697">—</span> : <div className="flex justify-center" data-id="element-1698">
                            {campaign.artworkStatus === 'approved' ? <div className="w-3 h-3 rounded-full bg-green-500" title="Artwork Approved" data-id="element-1699" /> : campaign.artworkStatus === 'uploaded' ? <div className="w-3 h-3 rounded-full bg-blue-500" title="Artwork Uploaded" data-id="element-1700" /> : campaign.artworkStatus === 'partial' ? <div className="w-3 h-3 rounded-full bg-amber-500" title="Partial Artwork" data-id="element-1701" /> : <div className="w-3 h-3 rounded-full bg-red-500" title="No Artwork" data-id="element-1702" />}
                          </div>}
                      </td>
                      <td className="px-4 md:px-6 py-3.5 text-center" data-id="element-1703">
                        {isUnavailable ? <Badge variant="default" data-id="element-1704">Booked</Badge> : campaign.hasAcceptedRequest ? <Badge variant="available" data-id="element-1705">Confirmed</Badge> : <Badge variant="booked" data-id="element-1706">Booked</Badge>}
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div> : <div className="overflow-x-auto" data-id="element-1707">
            <div className="min-w-[1100px]" data-id="element-1708">
              {/* Week header row */}
              <div className="flex sticky top-0 z-10 bg-surface border-b border-border" data-id="element-1709">
                <div className="w-48 flex-shrink-0 px-4 py-3 border-r border-border" data-id="element-1710">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1711">
                    Brand
                  </span>
                </div>
                <div className="flex-1 grid grid-cols-12" data-id="element-1712">
                  {weeks.map(w => {
                const dates = generateWeekDatesForCalendar(w);
                return <div key={w} className="px-1 py-3 text-center border-r border-border last:border-r-0" data-id="element-1713">
                        <div className="text-xs font-semibold text-text-primary" data-id="element-1714">
                          Wk {w}
                        </div>
                        <div className="text-[10px] text-text-tertiary mt-0.5" data-id="element-1715">
                          {dates.start}
                        </div>
                      </div>;
              })}
                </div>
              </div>

              {/* Brand rows */}
              {calendarData.length === 0 ? <div className="py-16 text-center text-sm text-text-tertiary" data-id="element-1716">
                  No campaigns to display
                </div> : calendarData.map(({
            brand,
            campaigns: brandCampaigns
          }) => {
            const isUnavailableRow = brand === 'Unavailable';
            const color = isUnavailableRow ? {
              bg: 'bg-gray-100 border-gray-200',
              text: 'text-gray-400',
              dot: 'bg-gray-300'
            } : getBrandColor(brand, allBrandNames);
            return <div key={brand} className={`flex border-b border-border transition-colors ${isUnavailableRow ? 'bg-surface/20' : 'hover:bg-surface/30'}`} data-id="element-1717">
                      {/* Brand label */}
                      <div className="w-48 flex-shrink-0 px-4 py-3 border-r border-border flex items-center gap-2" data-id="element-1718">
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${color.dot}`} data-id="element-1719" />
                        <div className="min-w-0" data-id="element-1720">
                          <div className={`text-sm font-medium truncate ${isUnavailableRow ? 'text-text-tertiary italic' : 'text-text-primary'}`} data-id="element-1721">
                            {isUnavailableRow ? 'Other Brands' : brand}
                          </div>
                          {!isUnavailableRow && <div className="text-[10px] text-text-tertiary" data-id="element-1722">
                              {brandCampaigns.reduce((sum, c) => sum + c.slotCount, 0)}{' '}
                              slots · {brandCampaigns.length} week
                              {brandCampaigns.length !== 1 ? 's' : ''}
                            </div>}
                          {isUnavailableRow && <div className="text-[10px] text-text-tertiary" data-id="element-1723">
                              {brandCampaigns.length} week
                              {brandCampaigns.length !== 1 ? 's' : ''} booked
                            </div>}
                        </div>
                      </div>

                      {/* Week cells */}
                      <div className="flex-1 grid grid-cols-12" data-id="element-1724">
                        {weeks.map(w => {
                  const campaign = brandCampaigns.find(c => c.weekNumber === w);
                  if (!campaign) {
                    return <div key={w} className="border-r border-border last:border-r-0 bg-white" data-id="element-1725" />;
                  }
                  const isHovered = !isUnavailableRow && hoveredCampaign === campaign.id;
                  return <div key={w} className="border-r border-border last:border-r-0 p-1 relative" onMouseEnter={() => !isUnavailableRow && setHoveredCampaign(campaign.id)} onMouseLeave={() => setHoveredCampaign(null)} data-id="element-1726">
                              {isUnavailableRow ? <div className="h-full min-h-[52px] rounded-md border border-gray-200 bg-gray-50 px-1.5 py-1 cursor-default flex items-center justify-center" data-id="element-1727">
                                  <span className="text-[10px] font-medium text-gray-400 text-center" data-id="element-1728">
                                    Unavailable
                                  </span>
                                </div> : <div className={`h-full min-h-[52px] rounded-md border px-1.5 py-1 cursor-default transition-all ${color.bg} ${isHovered ? 'shadow-md scale-[1.02]' : ''}`} data-id="element-1729">
                                  <div className={`text-[10px] font-semibold truncate ${color.text}`} data-id="element-1730">
                                    {campaign.stores.length} store
                                    {campaign.stores.length !== 1 ? 's' : ''}
                                  </div>
                                  <div className="flex gap-0.5 mt-0.5 flex-wrap" data-id="element-1731">
                                    {campaign.mediaTypes.map(mt => <span key={mt} className={`text-[9px] ${color.text} opacity-70`} data-id="element-1732">
                                        {mt === 'Digital Outposts' ? 'OUT' : mt === 'Digital Drive Aisle' ? 'DA' : 'WIN'}
                                      </span>)}
                                  </div>
                                  {user.role !== 'admin' && <div className={`text-[9px] font-medium mt-0.5 ${color.text} opacity-80`} data-id="element-1733">
                                      {formatCurrency(campaign.totalRevenue)}
                                    </div>}
                                </div>}

                              {/* Hover tooltip */}
                              {isHovered && <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-white border border-border shadow-lg rounded-sm p-3 pointer-events-none" data-id="element-1734">
                                  <div className="text-xs font-semibold text-text-primary mb-1" data-id="element-1735">
                                    {brand}
                                  </div>
                                  {campaign.campaignName && <div className="text-[11px] text-text-secondary mb-1.5" data-id="element-1736">
                                      {campaign.campaignName}
                                    </div>}
                                  <div className="text-[11px] text-text-secondary space-y-0.5" data-id="element-1737">
                                    <div data-id="element-1738">
                                      Week {campaign.weekNumber}
                                      {campaign.endWeekNumber && campaign.endWeekNumber !== campaign.weekNumber ? ` – ${campaign.endWeekNumber}` : ''}{' '}
                                      ·{' '}
                                      {formatWeekDates(campaign.weekStartDate, campaign.weekEndDate)}
                                    </div>
                                    <div data-id="element-1739">
                                      {campaign.slotCount} slots across{' '}
                                      {campaign.stores.length} store
                                      {campaign.stores.length !== 1 ? 's' : ''}
                                    </div>
                                    {user.role !== 'admin' && <div className="font-medium" data-id="element-1740">
                                        {formatCurrency(campaign.totalRevenue)}
                                      </div>}
                                  </div>
                                  <div className="flex flex-wrap gap-1 mt-2" data-id="element-1741">
                                    {campaign.mediaTypes.map(mt => <span key={mt} className="text-[10px] px-1.5 py-0.5 bg-surface-warm border border-border rounded-sm text-text-secondary" data-id="element-1742">
                                        {mt.replace('Digital ', '')}
                                      </span>)}
                                  </div>
                                  <div className="mt-1.5 text-[10px] text-text-tertiary" data-id="element-1743">
                                    {campaign.stores.slice(0, 3).map(s => s.name).join(', ')}
                                    {campaign.stores.length > 3 && ` +${campaign.stores.length - 3} more`}
                                  </div>
                                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-border" data-id="element-1744" />
                                </div>}
                            </div>;
                })}
                      </div>
                    </div>;
          })}
            </div>
          </div>}
      </main>
    </div>;
}